import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { THROUGHPUT_DEFAULTS, throughputSchema, type ThroughputState } from "./throughputSchema";

const ARRAY_KEYS = new Set(["requestType", "client", "lob", "eng", "assigned", "team", "division", "manager", "country"]);
const NUMBER_KEYS = new Set(["months"]);
const BOOLEAN_KEYS = new Set(["onboardingsOnly", "redeployment", "showBacklog"]);

function parseFromSearchParams(params: URLSearchParams): ThroughputState {
  const raw: Record<string, unknown> = {};
  for (const key of ARRAY_KEYS) {
    const values = params.getAll(key);
    if (values.length > 0) raw[key] = values;
  }
  for (const [key, value] of params.entries()) {
    if (ARRAY_KEYS.has(key)) continue;
    if (NUMBER_KEYS.has(key)) {
      const n = Number(value);
      if (!Number.isNaN(n)) raw[key] = n;
    } else if (BOOLEAN_KEYS.has(key)) {
      raw[key] = value === "true";
    } else {
      raw[key] = value;
    }
  }
  const result = throughputSchema.safeParse(raw);
  if (result.success) return result.data;
  if (typeof console !== "undefined") {
    console.warn("[throughput] invalid URL state:", result.error.issues);
  }
  const bad = new Set(result.error.issues.map((i) => i.path[0] as string));
  for (const k of bad) delete raw[k];
  return throughputSchema.parse(raw);
}

function serializeToSearchParams(state: ThroughputState): URLSearchParams {
  const params = new URLSearchParams();
  const def = THROUGHPUT_DEFAULTS as Record<string, unknown>;
  for (const [key, value] of Object.entries(state) as [keyof ThroughputState, unknown][]) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      for (const v of value) params.append(key, String(v));
      continue;
    }
    if (typeof value === "boolean") {
      if (value === def[key]) continue;
      params.set(key, value ? "true" : "false");
      continue;
    }
    if (def[key] === value) continue;
    params.set(key, String(value));
  }
  return params;
}

export function useThroughputUrlState(): [ThroughputState, (next: Partial<ThroughputState>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseFromSearchParams(searchParams), [searchParams]);
  const set = useCallback(
    (next: Partial<ThroughputState>) => {
      const merged = throughputSchema.parse({ ...state, ...next });
      setSearchParams(serializeToSearchParams(merged));
    },
    [state, setSearchParams],
  );
  return [state, set];
}
